import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "@phosphor-icons/react";
import { assetUrl } from "@/lib/api";

const PAGE_SIZE = 6;
const AUTO_ROTATE_MS = 8000;

function formatDate(d) {
    try {
        return new Date(d).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
    } catch { return ""; }
}

function chunk(arr, size) {
    const out = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
}

// Compact news card used inside the carousel slides (5 per row).
function CarouselCard({ post }) {
    return (
        <Link
            to={`/post/${post.slug}`}
            data-testid={`carousel-news-card-${post.slug}`}
            className="tz-card group block overflow-hidden bg-[#0A0A0A] h-full"
        >
            <div className="relative aspect-[16/11] overflow-hidden bg-zinc-900">
                {post.cover_image ? (
                    <img
                        src={assetUrl(post.cover_image)}
                        alt={post.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                ) : (
                    <div className="w-full h-full bg-zinc-900 grid-overlay" />
                )}
                <div className="absolute top-3 left-3">
                    <span className="font-mono text-[9px] font-bold uppercase tracking-[0.25em] bg-[#FFD000] text-black px-2 py-1">
                        News
                    </span>
                </div>
            </div>
            <div className="p-4">
                <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 mb-2 truncate">
                    {formatDate(post.created_at)}
                </div>
                <h3 className="font-display text-white text-lg leading-[1.1] tracking-tight line-clamp-3 group-hover:text-[#FFD000] transition-colors">
                    {post.title}
                </h3>
                <div className="mt-4 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#FFD000]">
                    Read <ArrowUpRight size={12} weight="bold" className="group-hover:rotate-45 transition-transform" />
                </div>
            </div>
        </Link>
    );
}

export default function LatestNewsCarousel({ posts }) {
    const pages = useMemo(() => chunk(posts, PAGE_SIZE), [posts]);
    const [page, setPage] = useState(0);
    const [paused, setPaused] = useState(false);
    const [dir, setDir] = useState(1);

    useEffect(() => {
        if (paused || pages.length < 2) return;
        const t = setInterval(() => {
            setDir(1);
            setPage(p => (p + 1) % pages.length);
        }, AUTO_ROTATE_MS);
        return () => clearInterval(t);
    }, [paused, pages.length]);

    if (!pages.length) return null;

    const goTo = (next) => {
        setDir(next > page ? 1 : -1);
        setPage((next + pages.length) % pages.length);
    };

    const current = pages[page] || [];
    // Always render up to 3 cards per row so the layout stays a clean 2×3 grid.
    // When there are fewer than 3 posts we collapse to that count for a tidy single row.
    const colsClass = (() => {
        switch (Math.min(current.length, 3)) {
            case 3: return "md:grid-cols-3";
            case 2: return "md:grid-cols-2";
            default: return "md:grid-cols-1";
        }
    })();

    return (
        <div
            data-testid="latest-news-carousel"
            className="relative"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            <div className="overflow-hidden">
                <AnimatePresence mode="wait" custom={dir}>
                    <motion.div
                        key={page}
                        custom={dir}
                        initial={{ opacity: 0, x: dir * 60 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: dir * -60 }}
                        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        className={`grid grid-cols-1 sm:grid-cols-2 ${colsClass} gap-px bg-zinc-800`}
                    >
                        {current.map(p => (
                            <CarouselCard key={p.id} post={p} />
                        ))}
                    </motion.div>
                </AnimatePresence>
            </div>

            {pages.length > 1 && (
                <div className="mt-8 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        {pages.map((_, i) => (
                            <button
                                key={i}
                                onClick={() => goTo(i)}
                                data-testid={`latest-news-dot-${i}`}
                                aria-label={`Show news page ${i + 1}`}
                                className={`h-[3px] transition-all duration-300 ${
                                    i === page ? "w-10 bg-[#FFD000]" : "w-5 bg-zinc-700 hover:bg-zinc-500"
                                }`}
                            />
                        ))}
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                            {String(page + 1).padStart(2, "0")} / {String(pages.length).padStart(2, "0")}
                        </span>
                        <button
                            onClick={() => goTo(page - 1)}
                            data-testid="latest-news-prev"
                            aria-label="Previous news"
                            className="h-10 w-10 grid place-items-center border border-zinc-800 text-zinc-400 hover:text-black hover:bg-[#FFD000] hover:border-[#FFD000] transition-colors"
                        >
                            <ArrowLeft size={16} weight="bold" />
                        </button>
                        <button
                            onClick={() => goTo(page + 1)}
                            data-testid="latest-news-next"
                            aria-label="Next news"
                            className="h-10 w-10 grid place-items-center border border-zinc-800 text-zinc-400 hover:text-black hover:bg-[#FFD000] hover:border-[#FFD000] transition-colors"
                        >
                            <ArrowRight size={16} weight="bold" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
